import React, { useState, useCallback } from 'react';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import Skeleton from '@material-ui/lab/Skeleton';

import useOpenJobs from '../hooks/queries/useOpenJobs';
import formatOpenJobs from '../functions/formatOpenJobs';
import model from '../assets/models/model';
import JobTable from '../JobTable';
import JobDialog from '../JobDialog';
import DefaultColumnFilter from '../../react-table/DefaultColumnFilter';
import TableToolbar from '../../react-table/TableToolbar';
import ApplyButton from './ApplyButton';

const SKELETON_ROWS = 5;

const PublicJob = () => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState();

  const { data: openJobs, isLoading } = useOpenJobs();

  const defaultColumn = React.useMemo(
    () => ({
      Filter: DefaultColumnFilter, // sets up default column filter input type
    }),
    []
  );

  // Initial state of table parameters
  const initialState = React.useMemo(
    () => ({
      pageSize: 25,
      sortBy: [
        {
          id: model?.jobId?.id,
          desc: true,
        },
      ],
      hiddenColumns: [
        model.hourlyRate.id,
        model.shift.id,
        model.schedule.id,
        model.teleworking.id,
        model.usCitizenshipRequired.id,
        model.travel.id,
        model.travelPercentage.id,
        model.educationRequired.id,
        model.majorSpecialization.id,
        model.jobDescription.id,
      ],
    }),
    []
  );

  const columns = React.useMemo(() => {
    return [
      {
        Header: model.jobId.name,
        accessor: model.jobId.id,
      },
      {
        Header: model.jobTitle.name,
        accessor: model.jobTitle.id,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          // eslint-disable-next-line react/prop-types
          <div style={{ maxWidth: 200 }}>{row.original.jobTitle}</div>
        ),
      },
      {
        Header: model.jobLocation.name,
        accessor: model.jobLocation.id,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => (
          // eslint-disable-next-line react/prop-types
          <div style={{ maxWidth: 200 }}>{row.original.jobLocation}</div>
        ),
      },
      {
        Header: model.minimumClearance.name,
        accessor: model.minimumClearance.id,
      },
      {
        Header: model.polygraphType.name,
        accessor: model.polygraphType.id,
      },
      {
        Header: model.daysOpen.name,
        accessor: model.daysOpen.id,
      },
      {
        id: 'apply',
        disableFilters: true,
        // eslint-disable-next-line react/prop-types
        Cell: ({ row }) => {
          // eslint-disable-next-line react/prop-types
          const jobId = row?.original?.jobId;
          // eslint-disable-next-line react/prop-types
          const jobTitle = row?.original?.jobTitle;
          return <ApplyButton jobId={jobId} jobTitle={jobTitle} />;
        },
      },
      {
        id: model.hourlyRate.id,
        accessor: model.hourlyRate.id,
      },
      {
        id: model.shift.id,
        accessor: model.shift.id,
      },
      {
        id: model.schedule.id,
        accessor: model.schedule.id,
      },
      {
        id: model.teleworking.id,
        accessor: model.teleworking.id,
      },
      {
        id: model.usCitizenshipRequired.id,
        accessor: model.usCitizenshipRequired.id,
      },
      {
        id: model.travel.id,
        accessor: model.travel.id,
      },
      {
        id: model.travelPercentage.id,
        accessor: model.travelPercentage.id,
      },
      {
        id: model.educationRequired.id,
        accessor: model.educationRequired.id,
      },
      {
        id: model.majorSpecialization.id,
        accessor: model.majorSpecialization.id,
      },
      {
        id: model.jobDescription.id,
        accessor: model.jobDescription.id,
      },
    ];
  }, []);

  const tableData = React.useMemo(
    () =>
      isLoading ? Array(SKELETON_ROWS).fill({}) : formatOpenJobs(openJobs),
    [isLoading, openJobs]
  );

  const tableColumns = React.useMemo(
    () =>
      isLoading
        ? columns.map((column) => ({
            ...column,
            Cell: <Skeleton />,
          }))
        : columns,
    [isLoading, columns]
  );

  const tableHookProps = useTable(
    {
      columns: tableColumns,
      data: tableData,
      defaultColumn,
      initialState,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect
  );

  const {
    preGlobalFilteredRows,
    setGlobalFilter,
    state: { selectedRowIds, globalFilter },
  } = tableHookProps;

  const onClose = useCallback(() => {
    setOpen(false);
  }, []);

  const onOpen = useCallback((rowData) => {
    setSelectedRow(rowData);
    setOpen(true);
  }, []);

  return (
    <>
      <div>
        <TableToolbar
          numSelected={Object.keys(selectedRowIds).length}
          preGlobalFilteredRows={preGlobalFilteredRows}
          setGlobalFilter={setGlobalFilter}
          globalFilter={globalFilter}
        />
        <JobTable
          tableHookProps={tableHookProps}
          tableColumns={tableColumns}
          onOpen={onOpen}
          columnClicksToIgnore={['apply']}
        />
        <JobDialog
          onClose={onClose}
          onComplete={() => {}}
          open={open}
          selectedJobData={selectedRow}
          disabled
        />
      </div>
    </>
  );
};

export default PublicJob;
